// src/modules/Careers/JobDetail.js
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import { supabase } from '../../config/supabase';
import { useTranslation } from '../../contexts/LanguageContext';
import ReactMarkdown from 'react-markdown';
import {
  FaMapMarkerAlt,
  FaBriefcase,
  FaClock,
  FaLaptopHouse,
  FaArrowLeft,
  FaShareAlt,
  FaLinkedin,
  FaTwitter,
  FaWhatsapp,
  FaCheck,
  FaGraduationCap,
  FaGift,
  FaMoneyBillWave
} from 'react-icons/fa';

// Styled Components
const PageContainer = styled.div`
  min-height: 100vh;
  background: #fff;
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, #111 0%, #2a2a2a 100%);
  padding: 120px 24px 60px;
`;

const HeroContent = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

const BackLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 8px;
  color: rgba(255, 255, 255, 0.7);
  text-decoration: none;
  font-size: 14px;
  margin-bottom: 24px;
  transition: color 200ms;
  width: fit-content;

  &:hover {
    color: white;
  }
`;

const JobDepartment = styled.span`
  display: inline-block;
  padding: 6px 12px;
  background: rgba(255, 255, 255, 0.15);
  color: white;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-radius: 4px;
  margin-bottom: 16px;
`;

const JobTitle = styled.h1`
  font-size: clamp(28px, 4vw, 42px);
  font-weight: 700;
  color: white;
  line-height: 1.2;
  margin-bottom: 20px;
`;

const JobMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
`;

const MetaItem = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);

  svg {
    font-size: 14px;
    opacity: 0.7;
  }
`;

const ContentWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 380px;
  gap: 40px;
  max-width: 1200px;
  margin: 0 auto;
  padding: 60px 24px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const MainContent = styled.div``;

const Sidebar = styled.div`
  @media (max-width: 1024px) {
    order: -1;
  }
`;

const Section = styled.section`
  margin-bottom: 40px;
`;

const SectionTitle = styled.h2`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 20px;
  font-weight: 600;
  color: #111;
  margin-bottom: 16px;

  svg {
    font-size: 18px;
    color: #666;
  }
`;

const MarkdownContent = styled.div`
  font-size: 16px;
  line-height: 1.8;
  color: #444;

  h1, h2, h3, h4 {
    color: #111;
    margin-top: 24px;
    margin-bottom: 12px;
  }

  p {
    margin-bottom: 16px;
  }

  ul, ol {
    margin-bottom: 16px;
    padding-left: 24px;
  }

  li {
    margin-bottom: 8px;
  }

  strong {
    color: #111;
  }
`;

const ApplyCard = styled.div`
  background: #f8f8f8;
  border-radius: 16px;
  padding: 28px;
  position: sticky;
  top: 100px;
`;

const ApplyTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #111;
  margin-bottom: 20px;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: #555;
  margin-bottom: 6px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 14px;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 14px;
  transition: all 200ms;

  &:focus {
    outline: none;
    border-color: #111;
    box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.05);
  }

  &::placeholder {
    color: #999;
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 12px 14px;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 14px;
  min-height: 120px;
  resize: vertical;
  transition: all 200ms;

  &:focus {
    outline: none;
    border-color: #111;
    box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.05);
  }

  &::placeholder {
    color: #999;
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 14px 24px;
  background: #111;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 200ms;

  &:hover:not(:disabled) {
    background: #333;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SuccessMessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 20px;

  svg {
    font-size: 48px;
    color: #10b981;
    margin-bottom: 16px;
  }

  h4 {
    font-size: 18px;
    font-weight: 600;
    color: #111;
    margin-bottom: 8px;
  }

  p {
    font-size: 14px;
    color: #666;
  }
`;

const ShareSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #e5e5e5;
`;

const ShareLabel = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #666;

  svg {
    font-size: 14px;
  }
`;

const ShareButton = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: white;
  color: #666;
  border: 1px solid #e5e5e5;
  transition: all 200ms;

  &:hover {
    background: #111;
    color: white;
    border-color: #111;
  }
`;

const SalaryBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: #f0fdf4;
  color: #166534;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 20px;

  svg {
    font-size: 16px;
  }
`;

const LoadingState = styled.div`
  min-height: 60vh;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  color: #666;
`;

const NotFoundState = styled.div`
  min-height: 60vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 40px 24px;
`;

const NotFoundTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin-bottom: 12px;
`;

const NotFoundText = styled.p`
  font-size: 16px;
  color: #666;
  margin-bottom: 24px;
`;

const BackButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: #111;
  color: white;
  text-decoration: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  transition: background 200ms;

  &:hover {
    background: #333;
  }
`;

const JobDetail = () => {
  const { slug } = useParams();
  const { t, language } = useTranslation();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    linkedin_url: '',
    portfolio_url: '',
    cover_letter: ''
  });

  useEffect(() => {
    fetchJob();
    incrementViews();
  }, [slug]);

  const fetchJob = async () => {
    try {
      const { data, error } = await supabase
        .from('jobs')
        .select('*')
        .eq('slug', slug)
        .eq('status', 'active')
        .single();

      if (error) throw error;
      setJob(data);
    } catch (error) {
      console.error('Erro ao buscar vaga:', error);
      setJob(null);
    } finally {
      setLoading(false);
    }
  };

  const incrementViews = async () => {
    try {
      await supabase.rpc('increment_job_views', { job_slug: slug });
    } catch (error) {
      console.error('Erro ao incrementar views:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email) return;

    setSubmitting(true);
    try {
      // Inserir candidatura
      const { error: applicationError } = await supabase
        .from('job_applications')
        .insert([{
          job_id: job.id,
          name: formData.name,
          email: formData.email,
          phone: formData.phone || null,
          linkedin_url: formData.linkedin_url || null,
          portfolio_url: formData.portfolio_url || null,
          cover_letter: formData.cover_letter || null,
          status: 'new'
        }]);

      if (applicationError) throw applicationError;

      // Incrementar contador de candidaturas
      await supabase.rpc('increment_job_applications', { job_uuid: job.id });

      setSubmitted(true);
    } catch (error) {
      console.error('Erro ao enviar candidatura:', error);
      alert(t('careers.form.errorMessage'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Helper para obter conteúdo traduzido
  const getTranslatedField = (field) => {
    if (!job) return '';
    if (language === 'en' && job[`${field}_en`]) {
      return job[`${field}_en`];
    }
    if (language === 'es' && job[`${field}_es`]) {
      return job[`${field}_es`];
    }
    return job[field];
  };

  const getTypeLabel = (type) => {
    const labels = {
      'full-time': t('careers.types.fullTime'),
      'part-time': t('careers.types.partTime'),
      'contract': t('careers.types.contract'),
      'internship': t('careers.types.internship'),
      'freelance': t('careers.types.freelance'),
    };
    return labels[type] || type;
  };

  const getModalityLabel = (modality) => {
    const labels = {
      'on-site': t('careers.modalities.onSite'),
      'remote': t('careers.modalities.remote'),
      'hybrid': t('careers.modalities.hybrid'),
    };
    return labels[modality] || modality;
  };

  const getExperienceLabel = (level) => {
    const labels = {
      'intern': t('careers.experience.intern'),
      'junior': t('careers.experience.junior'),
      'mid': t('careers.experience.mid'),
      'senior': t('careers.experience.senior'),
      'lead': t('careers.experience.lead'),
    };
    return labels[level] || level;
  };

  const getShareUrl = () => encodeURIComponent(window.location.href);
  const getShareText = () => encodeURIComponent(`${getTranslatedField('title')} - Vega Robotics`);

  if (loading) {
    return (
      <PageContainer>
        <LoadingState>{t('general.loading')}</LoadingState>
      </PageContainer>
    );
  }

  if (!job) {
    return (
      <PageContainer>
        <NotFoundState>
          <NotFoundTitle>{t('careers.notFoundTitle')}</NotFoundTitle>
          <NotFoundText>{t('careers.notFoundText')}</NotFoundText>
          <BackButton to="/carreiras">
            <FaArrowLeft />
            {t('careers.backToJobs')}
          </BackButton>
        </NotFoundState>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <HeroSection>
        <HeroContent>
          <BackLink to="/carreiras">
            <FaArrowLeft />
            {t('careers.backToJobs')}
          </BackLink>

          <JobDepartment>{job.department}</JobDepartment>
          <JobTitle>{getTranslatedField('title')}</JobTitle>

          <JobMeta>
            <MetaItem>
              <FaMapMarkerAlt />
              {job.location}
            </MetaItem>
            <MetaItem>
              <FaBriefcase />
              {getTypeLabel(job.type)}
            </MetaItem>
            <MetaItem>
              <FaLaptopHouse />
              {getModalityLabel(job.modality)}
            </MetaItem>
            <MetaItem>
              <FaGraduationCap />
              {getExperienceLabel(job.experience_level)}
            </MetaItem>
          </JobMeta>
        </HeroContent>
      </HeroSection>

      <ContentWrapper>
        <MainContent>
          <Section>
            <SectionTitle>
              <FaBriefcase />
              {t('careers.aboutRole')}
            </SectionTitle>
            <MarkdownContent>
              <ReactMarkdown>{getTranslatedField('description')}</ReactMarkdown>
            </MarkdownContent>
          </Section>

          <Section>
            <SectionTitle>
              <FaGraduationCap />
              {t('careers.requirements')}
            </SectionTitle>
            <MarkdownContent>
              <ReactMarkdown>{getTranslatedField('requirements')}</ReactMarkdown>
            </MarkdownContent>
          </Section>

          {job.benefits && (
            <Section>
              <SectionTitle>
                <FaGift />
                {t('careers.benefits')}
              </SectionTitle>
              <MarkdownContent>
                <ReactMarkdown>{getTranslatedField('benefits')}</ReactMarkdown>
              </MarkdownContent>
            </Section>
          )}
        </MainContent>

        <Sidebar>
          <ApplyCard>
            {submitted ? (
              <SuccessMessage>
                <FaCheck />
                <h4>{t('careers.form.successTitle')}</h4>
                <p>{t('careers.form.successMessage')}</p>
              </SuccessMessage>
            ) : (
              <>
                <ApplyTitle>{t('careers.applyNow')}</ApplyTitle>

                {job.salary_range && (
                  <SalaryBadge>
                    <FaMoneyBillWave />
                    {job.salary_range}
                  </SalaryBadge>
                )}

                <form onSubmit={handleSubmit}>
                  <FormGroup>
                    <Label>{t('careers.form.name')} *</Label>
                    <Input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder={t('careers.form.namePlaceholder')}
                      required
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>{t('careers.form.email')} *</Label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder={t('careers.form.emailPlaceholder')}
                      required
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>{t('careers.form.phone')}</Label>
                    <Input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder={t('careers.form.phonePlaceholder')}
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>{t('careers.form.linkedin')}</Label>
                    <Input
                      type="url"
                      name="linkedin_url"
                      value={formData.linkedin_url}
                      onChange={handleChange}
                      placeholder="https://linkedin.com/in/seu-perfil"
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>{t('careers.form.portfolio')}</Label>
                    <Input
                      type="url"
                      name="portfolio_url"
                      value={formData.portfolio_url}
                      onChange={handleChange}
                      placeholder="https://github.com/seu-usuario"
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>{t('careers.form.coverLetter')}</Label>
                    <Textarea
                      name="cover_letter"
                      value={formData.cover_letter}
                      onChange={handleChange}
                      placeholder={t('careers.form.coverLetterPlaceholder')}
                    />
                  </FormGroup>

                  <SubmitButton type="submit" disabled={submitting}>
                    {submitting ? t('careers.form.sending') : t('careers.form.submit')}
                  </SubmitButton>
                </form>
              </>
            )}

            <ShareSection>
              <ShareLabel>
                <FaShareAlt />
                {t('careers.share')}:
              </ShareLabel>
              <ShareButton
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${getShareUrl()}`}
                target="_blank"
                rel="noopener noreferrer"
                title="LinkedIn"
              >
                <FaLinkedin />
              </ShareButton>
              <ShareButton
                href={`https://twitter.com/intent/tweet?url=${getShareUrl()}&text=${getShareText()}`}
                target="_blank"
                rel="noopener noreferrer"
                title="Twitter"
              >
                <FaTwitter />
              </ShareButton>
              <ShareButton
                href={`https://wa.me/?text=${getShareText()}%20${getShareUrl()}`}
                target="_blank"
                rel="noopener noreferrer"
                title="WhatsApp"
              >
                <FaWhatsapp />
              </ShareButton>
            </ShareSection>
          </ApplyCard>
        </Sidebar>
      </ContentWrapper>
    </PageContainer>
  );
};

export default JobDetail;
